// Vendor
import { ref } from 'vue';

// Personal libs
import { fullReload } from '@shared/lib/browser.lib';
import CheckAppVersion from '@shared/service/check-app-version/check-app-version.class';
import { LocalStorageEnum, ReloadMethodEnum } from '@shared/enum/localstorage.enum';

export function useBannerVersionVM() {
    /**
     * Reactive properties
     */
    const showUpdateApplicationBanner = ref<boolean>(false)
    const validBtnLabel = ref<string>()
    const localVersion = ref<string | undefined>();
    const remoteVersion = ref<string | undefined>();
    const reloadMethod = ref<ReloadMethodEnum>();

    /**
     * Methods
     */
    function onFullReload() {
        showUpdateApplicationBanner.value = false;
        fullReload(reloadMethod.value);
    }

    function enter(element) {
        const width = getComputedStyle(element).width;

        element.style.width = width;
        element.style.position = 'absolute';
        element.style.visibility = 'hidden';
        element.style.height = 'auto';

        const height = getComputedStyle(element).height;

        element.style.width = null;
        element.style.position = null;
        element.style.visibility = null;
        element.style.height = 0;

        // Force repaint to make sure the
        // animation is triggered correctly.
        getComputedStyle(element).height;

        // Trigger the animation.
        // We use `requestAnimationFrame` because we need
        // to make sure the browser has finished
        // painting after setting the `height`
        // to `0` in the line above.
        requestAnimationFrame(() => {
            element.style.height = height;
        });
    }

    function afterEnter(element) {
        element.style.height = 'auto';
    }

    function leave(element) {
        const height = getComputedStyle(element).height;
        
        element.style.height = height;

        // Force repaint to make sure the
        // animation is triggered correctly.
        getComputedStyle(element).height;

        requestAnimationFrame(() => {
            element.style.height = 0;
        });
    }

    async function init() {
        // Check app version and display dialog if needed
        const checkAppVersion = new CheckAppVersion();
        checkAppVersion.init()

        // Get reload method
        reloadMethod.value = getStorageReloadMethod()

        if (await checkAppVersion.hasToBeRefreshed()) {
            // Get versions (local and remote)
            localVersion.value = checkAppVersion.getLocalVersion();
            remoteVersion.value = await checkAppVersion.getRemoteVersion();

            if (reloadMethod.value == null || reloadMethod.value == ReloadMethodEnum.METHOD_1) {
                validBtnLabel.value = 'Mettre à jour'
                
                setStorageReloadMethod(ReloadMethodEnum.METHOD_2)
            } else if (reloadMethod.value == ReloadMethodEnum.METHOD_2) {
                validBtnLabel.value = 'Réessayer la mise à jour'
            }

            showUpdateApplicationBanner.value = true;
        } else if (reloadMethod.value == ReloadMethodEnum.METHOD_2) {
            setStorageReloadMethod(ReloadMethodEnum.METHOD_1)
        }
    }

    function getStorageReloadMethod(): ReloadMethodEnum | undefined {
        return ReloadMethodEnum.valueOf(localStorage.getItem(LocalStorageEnum.RELOAD_METHOD) ?? undefined)
    }

    function setStorageReloadMethod(reloadMethod: ReloadMethodEnum) {
        localStorage.setItem(LocalStorageEnum.RELOAD_METHOD, reloadMethod)
    }

    return {
        // Reactive var
        showUpdateApplicationBanner,
        localVersion,
        remoteVersion,
        validBtnLabel,

        // Methods
        onFullReload,
        enter,
        afterEnter,
        leave,
        init,
        getStorageReloadMethod,
        setStorageReloadMethod
    };
}